/**
 * «Секреты» (Spec 018, part C): the store inside a resource card — what the
 * resource keeps encrypted, who holds it, and the audit of every look.
 *
 * A value exists in the UI in two places only: the field where a person types
 * it, and after an explicit «Показать». Both keep it in component state: never
 * in a URL, browser storage, the react-query cache, a log line or an error
 * message. That is why the three calls that carry a value (create, rotate,
 * reveal) are plain handlers here, not mutations: react-query keeps a
 * mutation's variables and answer in its cache after it settles.
 */

import { useEffect, useId, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Check,
  Copy,
  Eye,
  EyeOff,
  History,
  PencilLine,
  Plus,
  RefreshCw,
  ShieldOff,
  Trash2,
  UserPlus,
} from "lucide-react";

import { ApiError, VersionConflictError, newIdempotencyKey } from "@/api/client";
import { AgentConnections, Me, Secrets, Users } from "@/api/resources";
import type {
  MySecret,
  ResourceOut,
  SecretEventKind,
  SecretEventOut,
  SecretGrantOut,
  SecretOut,
} from "@/api/types";
import { Button } from "@/components/ui/Button";
import { Card } from "@/components/ui/Card";
import { DateField, SelectField, TextArea, TextField } from "@/components/ui/Field";
import { Section } from "@/components/ui/Section";
import { useToast } from "@/components/ui/Toast";

/** A revealed value leaves the screen on its own after this long. */
export const REVEAL_MS = 60_000;
const COPIED_MS = 2_000;
/** The server's limit on a value. */
const VALUE_LIMIT = 65_536;

export const STORE_OFF =
  "Хранилище выключено: на сервере нет мастер-ключа. Значения не открываются и не " +
  "сохраняются; выдачи и журнал работают.";

const NAMES_ARE_PUBLIC = "Название и описание видят все участники и их агенты";

const EDGE_SPACE =
  "В начале или в конце есть пробел или перенос строки — он сохранится как есть.";

const LOST_REPLY =
  "Ответ сервера не дошёл. Нажми «Сохранить» ещё раз — второй секрет не появится.";

const day = (iso: string) => new Date(iso).toLocaleDateString("ru-RU");
const moment = (iso: string) => new Date(iso).toLocaleString("ru-RU");
const capital = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);

/** The server's own words for a refusal, ours for a lost connection. Never the request. */
const errorText = (e: unknown) =>
  e instanceof ApiError ? e.message : "Нет связи с сервером. Попробуй ещё раз.";

/** The server refused: the same request would be refused again, so a resend is a new attempt. */
const refused = (e: unknown) =>
  e instanceof ApiError && e.status >= 400 && e.status < 500 && e.status !== 408;

/** «Костя · в Mini App», «Костя · подключение «Claude»», with the expiry if any. */
export function grantLabel(
  grant: Pick<SecretGrantOut, "user_name" | "token_id" | "token_name" | "expires_at">,
): string {
  const who = grant.user_name ?? "Участник";
  const how = !grant.token_id
    ? "в Mini App"
    : grant.token_name
      ? `подключение «${grant.token_name}»`
      : "подключение агента";
  const until = grant.expires_at ? ` · до ${day(grant.expires_at)}` : "";
  return `${who} · ${how}${until}`;
}

/** An agent's grant says so, with the person's words when the viewer may see them. */
export function grantedVia(
  grant: Pick<SecretGrantOut, "via_agent" | "confirmation_md">,
): string | null {
  if (!grant.via_agent) return null;
  return grant.confirmation_md
    ? `через агента, по словам: «${grant.confirmation_md}»`
    : "через агента";
}

const EVENT_LABELS: Record<SecretEventKind, string> = {
  created: "создал",
  updated: "переименовал",
  rotated: "сменил значение",
  granted: "выдал",
  revoked: "отозвал",
  revealed: "открыл в Mini App",
  link_issued: "агент получил ссылку",
  downloaded: "агент скачал",
  destroyed: "удалил",
  rewrapped: "перешифрован ключ",
};

/** An edit says what it changed: the audit keeps the field names in `note`. */
function eventVerb(event: SecretEventOut): string {
  if (event.kind === "updated" && event.note) {
    const fields = event.note.split(",").map((f) => f.trim());
    const renamed = fields.includes("name");
    const described = fields.includes("description_md");
    if (renamed && described) return "переименовал и поправил описание";
    if (described) return "поправил описание";
  }
  return EVENT_LABELS[event.kind] ?? "изменил";
}

/**
 * One audit line — who did what, to whom, how and when: «Костя выдал: Саша ·
 * 26.09.2026, 14:05», «Агент получил ссылку · Костя · через агента (Claude) · …».
 */
export function eventLine(event: SecretEventOut): string {
  const when = moment(event.created_at);
  if (event.kind === "rewrapped") {
    return `${capital(EVENT_LABELS.rewrapped)}${event.note ? ` (${event.note})` : ""} · ${when}`;
  }
  const who = event.actor_name ?? "Участник";
  const via = event.via_agent
    ? ` · через агента${event.connection_name ? ` (${event.connection_name})` : ""}`
    : "";
  if (event.kind === "link_issued" || event.kind === "downloaded") {
    return `${capital(EVENT_LABELS[event.kind])} · ${who}${via} · ${when}`;
  }
  const connection = event.subject_connection_name
    ? `, подключение «${event.subject_connection_name}»`
    : "";
  const whom = event.subject_user_id ? `: ${event.subject_name ?? "участник"}${connection}` : "";
  return `${who} ${eventVerb(event)}${whom}${via} · ${when}`;
}

export const HOW_LABELS: Record<MySecret["via"], string> = {
  owner: "владелец",
  person: "выдан мне",
  connection: "выдан подключению агента",
};

/** «владелец», «выдан мне · до 01.10.2026». */
export function howLabel(item: Pick<MySecret, "via" | "expires_at">): string {
  const until = item.expires_at ? ` · до ${day(item.expires_at)}` : "";
  return `${HOW_LABELS[item.via] ?? "выдан"}${until}`;
}

export function SecretsSection({ resource }: { resource: Pick<ResourceOut, "id"> }) {
  const qc = useQueryClient();
  const headingId = useId();
  const [adding, setAdding] = useState(false);
  const status = useQuery({ queryKey: ["secrets-status"], queryFn: Secrets.status });
  const secrets = useQuery({
    queryKey: ["secrets", resource.id],
    queryFn: () => Secrets.list(resource.id),
  });
  const storeOn = status.data?.enabled === true;
  const items = secrets.data ?? [];
  const refresh = () =>
    Promise.all([
      qc.invalidateQueries({ queryKey: ["secrets", resource.id] }),
      qc.invalidateQueries({ queryKey: ["my-secrets"] }),
      qc.invalidateQueries({ queryKey: ["secret-events"] }),
      // A refusal may say the key went away: the banner has to follow.
      qc.invalidateQueries({ queryKey: ["secrets-status"] }),
    ]);

  return (
    <section aria-labelledby={headingId} className="space-y-2 border-t border-sep pt-2">
      <h3 id={headingId} className="text-cell-title font-medium text-text">
        Секреты
      </h3>
      <p className="text-meta text-hint">
        Значения хранятся зашифрованными. Агент получает секрет одноразовой ссылкой по выдаче
        человека.
      </p>
      {status.data?.enabled === false && (
        <div
          role="status"
          className="flex items-start gap-2 rounded-card border border-dstr bg-dstr-soft px-3 py-2"
        >
          <ShieldOff className="mt-px h-4 w-4 shrink-0 text-dstr" aria-hidden="true" />
          <p className="text-meta text-dstr">{STORE_OFF}</p>
        </div>
      )}
      {status.isError && (
        <div role="alert" className="space-y-1">
          <p className="text-meta text-dstr">Не удалось узнать, включено ли хранилище.</p>
          <Button size="compact" variant="ghost" onClick={() => void status.refetch()}>
            Повторить
          </Button>
        </div>
      )}
      {secrets.isLoading && <p className="text-meta text-hint">Загружаем…</p>}
      {secrets.isError && (
        <div role="alert" className="space-y-1">
          <p className="text-meta text-dstr">Не удалось загрузить секреты.</p>
          <Button size="compact" variant="ghost" onClick={() => void secrets.refetch()}>
            Повторить
          </Button>
        </div>
      )}
      {secrets.isSuccess && items.length === 0 && !adding && (
        <p className="text-meta text-hint">Секретов пока нет.</p>
      )}
      {items.map((s) => (
        <SecretRow key={s.id} secret={s} storeOn={storeOn} onChanged={refresh} />
      ))}
      {storeOn &&
        (adding ? (
          <NewSecret resourceId={resource.id} onDone={() => setAdding(false)} onSaved={refresh} />
        ) : (
          <div>
            <Button size="compact" variant="ghost" onClick={() => setAdding(true)}>
              <Plus className="h-4 w-4" aria-hidden="true" />
              Добавить секрет
            </Button>
          </div>
        ))}
    </section>
  );
}

type Mode = "rename" | "rotate" | "grant" | "destroy";

export function SecretRow({
  secret,
  storeOn,
  onChanged,
}: {
  secret: SecretOut;
  /** The server has its master key: values open and save. */
  storeOn: boolean;
  onChanged: () => Promise<unknown>;
}) {
  const qc = useQueryClient();
  const toast = useToast();
  const nameId = useId();
  const valueId = useId();
  const logId = useId();
  const [mode, setMode] = useState<Mode | null>(null);
  const [showLog, setShowLog] = useState(false);
  // The value itself: here and nowhere else, until it is hidden.
  const [shown, setShown] = useState<string | null>(null);
  const [revealing, setRevealing] = useState(false);
  const [copied, setCopied] = useState(false);
  const [error, setError] = useState<string>();

  useEffect(() => {
    if (shown === null) return;
    // It leaves the screen on its own, and at once when the app is put away.
    const clear = () => {
      setShown(null);
      setCopied(false);
    };
    const timer = window.setTimeout(clear, REVEAL_MS);
    const away = () => {
      if (document.visibilityState === "hidden") clear();
    };
    document.addEventListener("visibilitychange", away);
    return () => {
      window.clearTimeout(timer);
      document.removeEventListener("visibilitychange", away);
    };
  }, [shown]);

  useEffect(() => {
    if (!copied) return;
    const timer = window.setTimeout(() => setCopied(false), COPIED_MS);
    return () => window.clearTimeout(timer);
  }, [copied]);

  const destroyed = secret.destroyed_at !== null;
  const canReveal = !destroyed && storeOn && secret.my.reveal;
  const visible = canReveal && shown !== null;
  const facts = [
    secret.owner_name && `Владелец: ${secret.owner_name}`,
    secret.rotated_at && `сменён ${day(secret.rotated_at)}`,
  ].filter(Boolean);

  const hide = () => {
    setShown(null);
    setCopied(false);
  };
  const open = (next: Mode) => {
    hide();
    setError(undefined);
    setMode(next);
  };
  /** Someone changed the secret meanwhile: show the new state before asking again. */
  const conflict = (what: string) => {
    setMode(null);
    setError(`Секрет изменили, пока ты смотрел. Перечитай и ${what} снова, если нужно.`);
    void onChanged();
  };

  const reveal = async () => {
    setError(undefined);
    setRevealing(true);
    try {
      const answer = await Secrets.reveal(secret.id);
      setShown(answer.value);
      // The audit now holds this look.
      void qc.invalidateQueries({ queryKey: ["secret-events", secret.id] });
    } catch (e) {
      setError(errorText(e));
      // Destroyed meanwhile, or the store went off: re-read the card and the banner.
      if (e instanceof ApiError && (e.status === 410 || e.status === 503)) void onChanged();
    } finally {
      setRevealing(false);
    }
  };

  const copy = async () => {
    if (shown === null) return;
    try {
      await navigator.clipboard.writeText(shown);
      setError(undefined);
      setCopied(true);
    } catch {
      setError("Не удалось скопировать. Выдели значение и скопируй вручную.");
    }
  };

  const destroy = useMutation({
    mutationFn: () => Secrets.destroy(secret.id, secret.version),
    onSuccess: async () => {
      setMode(null);
      await onChanged();
      toast.success("Секрет удалён");
    },
    onError: (e) => {
      if (e instanceof VersionConflictError) conflict("удали");
      else setError(errorText(e));
    },
  });

  return (
    <div role="group" aria-labelledby={nameId} className="space-y-1 border-t border-sep pt-2">
      <div className={destroyed ? "done-dim" : undefined}>
        <p id={nameId} className="break-words text-body font-medium text-text">
          {secret.name}
        </p>
        {secret.description_md && (
          <p className="whitespace-pre-wrap break-words text-meta text-hint">
            {secret.description_md}
          </p>
        )}
      </div>
      {secret.destroyed_at ? (
        <p className="text-meta text-hint">Удалён {day(secret.destroyed_at)}</p>
      ) : (
        facts.length > 0 && <p className="text-meta text-hint">{facts.join(" · ")}</p>
      )}
      {!destroyed && secret.grants.length > 0 && (
        <ul aria-label={`Кому выдан «${secret.name}»`} className="space-y-1">
          {secret.grants.map((g) => (
            <GrantRow
              key={g.id}
              secretId={secret.id}
              grant={g}
              canRevoke={secret.my.manage}
              onChanged={onChanged}
            />
          ))}
        </ul>
      )}
      {mode === "rename" ? (
        <RenameForm
          secret={secret}
          onDone={() => setMode(null)}
          onChanged={onChanged}
          onConflict={() => conflict("переименуй")}
        />
      ) : mode === "rotate" ? (
        <RotateForm
          secret={secret}
          onDone={() => setMode(null)}
          onChanged={onChanged}
          onConflict={() => conflict("смени значение")}
        />
      ) : mode === "grant" ? (
        <NewGrant secretId={secret.id} onDone={() => setMode(null)} onChanged={onChanged} />
      ) : mode === "destroy" ? (
        <div className="space-y-1">
          <p className="text-meta text-hint">
            Выдачи и ссылки отзовутся. Название и журнал останутся.
          </p>
          <div className="flex flex-wrap gap-1">
            {/* An irreversible action gets a second tap, as with an access. */}
            <Button size="compact" variant="ghost" onClick={() => setMode(null)}>
              Отмена
            </Button>
            <Button
              size="compact"
              variant="destructive-outline"
              // The label is long: let it wrap instead of leaving the card.
              className="h-fit min-h-touch whitespace-normal py-1.5"
              loading={destroy.isPending}
              loadingText="Удаляем…"
              onClick={() => destroy.mutate()}
            >
              Точно удалить: значение пропадёт навсегда
            </Button>
          </div>
        </div>
      ) : (
        <div className="flex flex-wrap gap-1">
          {canReveal && (
            // One button that turns into «Скрыть», so focus stays where it was.
            <Button
              size="compact"
              variant="ghost"
              aria-expanded={visible}
              aria-controls={visible ? valueId : undefined}
              loading={revealing}
              loadingText="Открываем…"
              onClick={visible ? hide : () => void reveal()}
            >
              {visible ? (
                <EyeOff className="h-4 w-4" aria-hidden="true" />
              ) : (
                <Eye className="h-4 w-4" aria-hidden="true" />
              )}
              {visible ? "Скрыть" : "Показать"}
            </Button>
          )}
          {!destroyed && secret.my.manage && (
            <Button size="compact" variant="ghost" onClick={() => open("grant")}>
              <UserPlus className="h-4 w-4" aria-hidden="true" />
              Выдать
            </Button>
          )}
          {!destroyed && secret.my.edit && (
            <Button size="compact" variant="ghost" onClick={() => open("rename")}>
              <PencilLine className="h-4 w-4" aria-hidden="true" />
              Переименовать
            </Button>
          )}
          {!destroyed && secret.my.edit && storeOn && (
            <Button size="compact" variant="ghost" onClick={() => open("rotate")}>
              <RefreshCw className="h-4 w-4" aria-hidden="true" />
              Сменить значение
            </Button>
          )}
          <Button
            size="compact"
            variant="ghost"
            aria-expanded={showLog}
            aria-controls={showLog ? logId : undefined}
            onClick={() => setShowLog(!showLog)}
          >
            <History className="h-4 w-4" aria-hidden="true" />
            Журнал
          </Button>
          {!destroyed && secret.my.edit && (
            <Button
              size="compact"
              variant="ghost"
              className="text-dstr"
              onClick={() => open("destroy")}
            >
              <Trash2 className="h-4 w-4" aria-hidden="true" />
              Удалить секрет
            </Button>
          )}
        </div>
      )}
      {visible && (
        <div id={valueId} role="group" aria-label={`Значение «${secret.name}»`} className="space-y-1">
          {/* Focusable, so a long key can be scrolled from the keyboard too. */}
          <pre
            tabIndex={0}
            className="max-h-48 overflow-auto whitespace-pre-wrap break-all rounded-compact border border-sep bg-bg p-2 font-mono text-meta text-text"
          >
            {shown}
          </pre>
          <p className="text-meta text-hint">Скроется через минуту. Открытие записано в журнал.</p>
          <div className="flex flex-wrap gap-1">
            <Button size="compact" variant="ghost" onClick={() => void copy()}>
              {copied ? (
                <Check className="h-4 w-4" aria-hidden="true" />
              ) : (
                <Copy className="h-4 w-4" aria-hidden="true" />
              )}
              {copied ? "Скопировано" : "Скопировать"}
            </Button>
          </div>
          <span aria-live="polite" className="sr-only">
            {copied ? "Скопировано" : ""}
          </span>
        </div>
      )}
      {error && (
        <p role="alert" className="text-meta text-dstr">
          {error}
        </p>
      )}
      {showLog && <SecretLog id={logId} secretId={secret.id} />}
    </div>
  );
}

function GrantRow({
  secretId,
  grant,
  canRevoke,
  onChanged,
}: {
  secretId: string;
  grant: SecretGrantOut;
  canRevoke: boolean;
  onChanged: () => Promise<unknown>;
}) {
  const toast = useToast();
  const [confirming, setConfirming] = useState(false);
  const [error, setError] = useState<string>();
  const revoke = useMutation({
    mutationFn: () => Secrets.revoke(secretId, grant.id, grant.version),
    onSuccess: async () => {
      setConfirming(false);
      await onChanged();
      toast.success("Выдача отозвана");
    },
    onError: (e) => {
      if (e instanceof VersionConflictError) {
        // Someone changed the grant: show the new one before asking again.
        setConfirming(false);
        setError("Выдачу изменили. Перечитай её и отзови снова, если нужно.");
        void onChanged();
      } else setError(errorText(e));
    },
  });
  const label = grantLabel(grant);
  const via = grantedVia(grant);

  return (
    <li className="space-y-1">
      <div className="flex items-center justify-between gap-2">
        <div className="min-w-0">
          <p className="break-words text-meta text-text">{label}</p>
          {via && <p className="break-words text-meta text-hint">{via}</p>}
        </div>
        {canRevoke && !confirming && (
          <Button
            size="compact"
            variant="ghost"
            aria-label={`Отозвать: ${label}`}
            onClick={() => {
              setError(undefined);
              setConfirming(true);
            }}
          >
            Отозвать
          </Button>
        )}
      </div>
      {confirming && (
        <div className="space-y-1">
          <p className="text-meta text-hint">
            Выдача закончится сразу, выданная ссылка перестанет работать. Кто уже получил значение,
            мог его сохранить — тогда смени значение.
          </p>
          <div className="flex gap-1">
            <Button size="compact" variant="ghost" onClick={() => setConfirming(false)}>
              Отмена
            </Button>
            <Button
              size="compact"
              variant="destructive-outline"
              loading={revoke.isPending}
              loadingText="Отзываем…"
              onClick={() => revoke.mutate()}
            >
              Точно отозвать
            </Button>
          </div>
        </div>
      )}
      {error && (
        <p role="alert" className="text-meta text-dstr">
          {error}
        </p>
      )}
    </li>
  );
}

/** Where a person types a value: never autocompleted, corrected or capitalised. */
function ValueField({
  label,
  value,
  onChange,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
}) {
  return (
    <TextArea
      label={label}
      value={value}
      rows={3}
      maxLength={VALUE_LIMIT}
      autoComplete="off"
      autoCorrect="off"
      autoCapitalize="off"
      spellCheck={false}
      className="font-mono"
      hint={/^\s|\s$/.test(value) ? EDGE_SPACE : undefined}
      onChange={(e) => onChange(e.target.value)}
    />
  );
}

export function NewSecret({
  resourceId,
  onDone,
  onSaved,
}: {
  resourceId: string;
  onDone: () => void;
  onSaved: () => Promise<unknown>;
}) {
  const toast = useToast();
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [value, setValue] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string>();
  // This form's retry key. A resend after a lost reply reuses it, so the server
  // hands back the first secret instead of storing a second one; any edit is a
  // new request with a new key. Only the key is kept here, never the body.
  const attempt = useRef<string | null>(null);
  const edited = () => {
    attempt.current = null;
  };

  const save = async () => {
    setError(undefined);
    setSaving(true);
    const key = (attempt.current ??= newIdempotencyKey());
    try {
      await Secrets.create(
        resourceId,
        { name: name.trim(), description_md: description.trim() || null, value },
        key,
      );
      attempt.current = null;
      setValue("");
      toast.success("Секрет сохранён");
      await onSaved();
      onDone();
    } catch (e) {
      // A refusal ends the attempt; after a lost reply the key waits for the resend.
      if (refused(e)) attempt.current = null;
      else void onSaved(); // it may have landed: the list will show it
      setError(e instanceof ApiError ? e.message : LOST_REPLY);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-2 border-t border-sep pt-2">
      <TextField
        label="Название"
        value={name}
        maxLength={200}
        placeholder="Например: токен GitHub для выкладки"
        onChange={(e) => {
          edited();
          setName(e.target.value);
        }}
      />
      <TextField
        label="Зачем нужен — необязательно"
        value={description}
        maxLength={4000}
        onChange={(e) => {
          edited();
          setDescription(e.target.value);
        }}
      />
      <ValueField
        label="Значение"
        value={value}
        onChange={(next) => {
          edited();
          setValue(next);
        }}
      />
      <p className="text-meta text-hint">
        {NAMES_ARE_PUBLIC}. Сам секрет пиши только в поле «Значение».
      </p>
      {error && (
        <p role="alert" className="text-meta text-dstr">
          {error}
        </p>
      )}
      <div className="flex gap-2">
        <Button
          size="compact"
          loading={saving}
          loadingText="Сохраняем…"
          disabled={!name.trim() || value.length === 0}
          onClick={() => void save()}
        >
          Сохранить
        </Button>
        <Button size="compact" variant="ghost" onClick={onDone}>
          Отмена
        </Button>
      </div>
    </div>
  );
}

function RenameForm({
  secret,
  onDone,
  onChanged,
  onConflict,
}: {
  secret: SecretOut;
  onDone: () => void;
  onChanged: () => Promise<unknown>;
  onConflict: () => void;
}) {
  const toast = useToast();
  // The version the edit began from: a change made meanwhile is a conflict, not an overwrite.
  const [base] = useState(secret.version);
  const [name, setName] = useState(secret.name);
  const [description, setDescription] = useState(secret.description_md ?? "");
  const [error, setError] = useState<string>();
  const nextName = name.trim();
  const nextDescription = description.trim() || null;
  const patch = {
    ...(nextName !== secret.name ? { name: nextName } : {}),
    ...(nextDescription !== (secret.description_md ?? null)
      ? { description_md: nextDescription }
      : {}),
  };
  const save = useMutation({
    mutationFn: () => Secrets.update(secret.id, { ...patch, version: base }),
    onSuccess: async () => {
      await onChanged();
      toast.success("Сохранено");
      onDone();
    },
    onError: (e) => {
      if (e instanceof VersionConflictError) onConflict();
      else setError(errorText(e));
    },
  });

  return (
    <div className="space-y-2">
      <TextField
        label="Название"
        value={name}
        maxLength={200}
        onChange={(e) => setName(e.target.value)}
      />
      <TextField
        label="Зачем нужен — необязательно"
        value={description}
        maxLength={4000}
        onChange={(e) => setDescription(e.target.value)}
      />
      <p className="text-meta text-hint">{NAMES_ARE_PUBLIC}: сам секрет сюда не пишут.</p>
      {error && (
        <p role="alert" className="text-meta text-dstr">
          {error}
        </p>
      )}
      <div className="flex gap-2">
        <Button
          size="compact"
          loading={save.isPending}
          loadingText="Сохраняем…"
          disabled={!nextName || Object.keys(patch).length === 0}
          onClick={() => {
            setError(undefined);
            save.mutate();
          }}
        >
          Сохранить
        </Button>
        <Button size="compact" variant="ghost" onClick={onDone}>
          Отмена
        </Button>
      </div>
    </div>
  );
}

function RotateForm({
  secret,
  onDone,
  onChanged,
  onConflict,
}: {
  secret: SecretOut;
  onDone: () => void;
  onChanged: () => Promise<unknown>;
  onConflict: () => void;
}) {
  const toast = useToast();
  const [base] = useState(secret.version);
  const [value, setValue] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string>();

  const save = async () => {
    setError(undefined);
    setSaving(true);
    try {
      await Secrets.rotate(secret.id, value, base);
      setValue("");
      toast.success("Значение сменено");
      await onChanged();
      onDone();
    } catch (e) {
      if (e instanceof VersionConflictError) onConflict();
      else setError(errorText(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-2">
      <ValueField label="Новое значение" value={value} onChange={setValue} />
      <p className="text-meta text-hint">
        Старое значение пропадёт. Выдачи останутся и получат новое.
      </p>
      {error && (
        <p role="alert" className="text-meta text-dstr">
          {error}
        </p>
      )}
      <div className="flex gap-2">
        <Button
          size="compact"
          loading={saving}
          loadingText="Меняем…"
          disabled={value.length === 0}
          onClick={() => void save()}
        >
          Сменить
        </Button>
        <Button size="compact" variant="ghost" onClick={onDone}>
          Отмена
        </Button>
      </div>
    </div>
  );
}

function NewGrant({
  secretId,
  onDone,
  onChanged,
}: {
  secretId: string;
  onDone: () => void;
  onChanged: () => Promise<unknown>;
}) {
  const toast = useToast();
  const me = useQuery({ queryKey: ["me"], queryFn: Me.get });
  const people = useQuery({ queryKey: ["users"], queryFn: Users.list });
  const [userId, setUserId] = useState("");
  // Empty: the person, who opens it in the Mini App.
  const [tokenId, setTokenId] = useState("");
  const [expires, setExpires] = useState("");
  const [error, setError] = useState<string>();
  // Only one's own connections are listed here, so only they can be chosen.
  const forMe = me.data !== undefined && userId === me.data.id;
  const connections = useQuery({
    queryKey: ["agent-connections"],
    queryFn: AgentConnections.list,
    enabled: forMe,
  });
  const toConnection = forMe && tokenId !== "";
  const grant = useMutation({
    mutationFn: () =>
      Secrets.grant(secretId, {
        user_id: userId,
        token_id: toConnection ? tokenId : null,
        // The whole chosen day, in the person's own time zone.
        expires_at: expires ? new Date(`${expires}T23:59:59`).toISOString() : null,
      }),
    onSuccess: async () => {
      await onChanged();
      toast.success("Секрет выдан");
      onDone();
    },
    onError: (e) => setError(errorText(e)),
  });
  const options = (people.data ?? [])
    .filter((u) => u.is_active)
    .map((u) => ({
      value: u.id,
      label: u.id === me.data?.id ? `${u.display_name} (я)` : u.display_name,
    }));
  const mine = (connections.data?.connections ?? [])
    .filter((c) => !c.revoked_at)
    .map((c) => ({ value: c.id, label: `Моему подключению агента «${c.name}»` }));

  return (
    <div className="space-y-2">
      <SelectField
        label="Кому"
        value={userId}
        onChange={(v) => {
          setUserId(v);
          setTokenId("");
        }}
        options={options}
        placeholder="Выбери участника"
      />
      {forMe && (
        <SelectField
          label="Как выдать"
          value={tokenId}
          onChange={setTokenId}
          options={mine}
          placeholder="Человеку — в Mini App"
        />
      )}
      {forMe && connections.isError && (
        <p role="alert" className="text-meta text-dstr">
          Не удалось загрузить подключения агентов.
        </p>
      )}
      {forMe && connections.isSuccess && mine.length === 0 && (
        <p className="text-meta text-hint">Подключений агентов пока нет.</p>
      )}
      <p className="text-meta text-hint">
        {toConnection
          ? "Агент получит секрет одноразовой ссылкой — значение не пройдёт через чат."
          : "Человек увидит значение в Mini App по кнопке «Показать»."}
      </p>
      <DateField
        label="До какого дня — необязательно"
        value={expires}
        onChange={setExpires}
        placeholder="Бессрочно"
      />
      {error && (
        <p role="alert" className="text-meta text-dstr">
          {error}
        </p>
      )}
      <div className="flex gap-2">
        <Button
          size="compact"
          loading={grant.isPending}
          loadingText="Выдаём…"
          disabled={!userId}
          onClick={() => {
            setError(undefined);
            grant.mutate();
          }}
        >
          Выдать
        </Button>
        <Button size="compact" variant="ghost" onClick={onDone}>
          Отмена
        </Button>
      </div>
    </div>
  );
}

function SecretLog({ id, secretId }: { id: string; secretId: string }) {
  const events = useQuery({
    queryKey: ["secret-events", secretId],
    queryFn: () => Secrets.events(secretId),
  });
  return (
    <div id={id} className="space-y-1">
      {events.isLoading && <p className="text-meta text-hint">Загружаем журнал…</p>}
      {events.isError && (
        <div role="alert" className="space-y-1">
          <p className="text-meta text-dstr">Не удалось загрузить журнал.</p>
          <Button size="compact" variant="ghost" onClick={() => void events.refetch()}>
            Повторить
          </Button>
        </div>
      )}
      {events.isSuccess && events.data.length === 0 && (
        <p className="text-meta text-hint">Записей пока нет.</p>
      )}
      {events.isSuccess && events.data.length > 0 && (
        <ul aria-label="Журнал секрета" className="space-y-1">
          {events.data.map((e) => (
            <li key={e.id} className="break-words text-meta text-hint">
              {eventLine(e)}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

/** «Мои секреты»: what the signed-in person can open, and why. */
export function MySecrets() {
  const mine = useQuery({ queryKey: ["my-secrets"], queryFn: Secrets.mine });
  if (!mine.isSuccess || mine.data.length === 0) return null;
  return (
    <Section title="Мои секреты">
      {mine.data.map((item) => (
        <Card key={item.secret.id} className="space-y-1">
          <p className="text-meta text-hint">{item.secret.resource_name ?? "Ресурс"}</p>
          <p className="text-cell-title font-medium text-text">{item.secret.name}</p>
          <p className="text-meta text-hint">{capital(howLabel(item))}</p>
        </Card>
      ))}
    </Section>
  );
}
